import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    Output,
} from '@angular/core';
import { finalize, take } from 'rxjs';
import { translate, TranslocoPipe } from '@jsverse/transloco';
import { UserService } from '@libs/vc-core-lib';
import { QRCodeComponent } from 'angularx-qrcode';
import { DialogComponent, InputComponent, NotificationService, ProgressSpinnerDirective } from 'vcp-common-ui-lib';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'vc-enable-mfa-dialog',
    templateUrl: './enable-mfa-dialog.component.html',
    styleUrls: ['./enable-mfa-dialog.component.scss'],
    imports: [TranslocoPipe, QRCodeComponent, DialogComponent, ProgressSpinnerDirective, InputComponent, FormsModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnableMfaDialogComponent {
    private readonly _notificationService: NotificationService = inject(NotificationService);
    private readonly _userService: UserService = inject(UserService);
    private readonly _changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

    dialogTitle = translate('ACCOUNT.ENABLE_MFA');
    primaryButtonText = translate('COMMON_UI.NEXT');

    currentStep: MFAStep = MFAStep.INTRO;
    mfaStep = MFAStep;

    loading: boolean = false;

    qrData: string = '';
    verificationCode!: string;

    @Input()
    set visible(value: boolean) {
        if (value !== this._visible) {
            this._visible = value;
            this.visibleChange.emit(this._visible);
        }

        if (value) {
            this.primaryButtonText = translate('COMMON_UI.NEXT');
            this.currentStep = MFAStep.INTRO;
            this.qrData = '';
        }

        if (!value) {
            this.verificationCode = '';
        }
    }
    get visible(): boolean {
        return this._visible;
    }
    private _visible = false;

    /* Whether to show the end explanation screen after verification.  Default is false */
    @Input()
    showEnd = false;

    @Input()
    showSecondaryTrigger = true;

    @Input()
    showCloseIcon = true;

    @Output()
    mfaComplete = new EventEmitter<void>();

    @Output()
    cancelMfa = new EventEmitter<void>();

    @Output()
    visibleChange = new EventEmitter<boolean>();

    nextStep() {
        if (this.currentStep === MFAStep.INTRO) {
            this.currentStep = MFAStep.QRCODE;
            this._beginMFA();
            return;
        }

        if (this.currentStep === MFAStep.QRCODE) {
            this.currentStep = MFAStep.VERIFY;
            return;
        }

        if (this.currentStep === MFAStep.VERIFY) {
            if (this.verificationCode || this.verificationCode !== '') {
                this.loading = true;
                this._verifyMFA(this.verificationCode).subscribe({
                    next: () => {
                        this._notificationService.success(translate('ACCOUNT.ENABLE_MFA.DETAILS.SUCCESS_ENABLING_MFA'));

                        if (this.showEnd) {
                            this.primaryButtonText = translate('CORE.USER.LOGOUT');
                            this.currentStep = MFAStep.END;
                            return;
                        }

                        this.visible = false;
                        this.currentStep = MFAStep.INTRO;
                        this.mfaComplete.emit();
                    },
                    error: () => this._notificationService.error(translate('ACCOUNT.ENABLE_MFA.DETAILS.ERROR')),
                });
            }
            return;
        }

        if (this.currentStep === MFAStep.END) {
            this.visible = false;
            this.mfaComplete.emit();
        }
    }

    private _beginMFA() {
        this._userService
            .beginMfa()
            .pipe(take(1))
            .subscribe({
                next: (qrData: string) => {
                    this.qrData = qrData;
                    this._changeDetectorRef.markForCheck();
                },
                error: () =>
                    this._notificationService.error(translate('ACCOUNT.ENABLE_MFA.DETAILS.ERROR_GENERATE_QRCODE')),
            });
    }

    private _verifyMFA(verificationCode: string) {
        return this._userService.verifyMfa(verificationCode).pipe(
            take(1),
            finalize(() => {
                this.loading = false;
                this._changeDetectorRef.markForCheck();
            })
        );
    }
}

enum MFAStep {
    INTRO = 'Intro',
    QRCODE = 'QRCode',
    VERIFY = 'Verify',
    END = 'End',
}
